import type { IIssueAssignee, IProfession, IUser } from '../../trusstor/index.js';

/**
 * Whether `user` may handle an issue of this profession — Trusstor's own rule
 * (`filterUsersByPermittedProfessions`): a user confined to some professions (a
 * Limited user) only within them; everyone else always. Assigning a Limited user
 * outside their professions would hand them an issue Trusstor's viewer filter then
 * hides from them.
 */
function mayHandle(user: IUser, professionId: string): boolean {
  const permitted = user.permissions?.permittedProfessionsIds;
  return !permitted?.length || permitted.includes(professionId);
}

/** Who may be assigned an issue of this profession. No profession yet: anyone. */
export function assigneesFor(users: readonly IUser[], professionId?: string): readonly IUser[] {
  return professionId ? users.filter((u) => mayHandle(u, professionId)) : users;
}

/** The professions an issue assigned to `assignee` may carry — the same rule seen
 *  from the other side. No assignee (or one not in the user list): every one. */
export function professionsFor(
  professions: readonly IProfession[],
  assignee: IUser | undefined,
): readonly IProfession[] {
  return assignee ? professions.filter((p) => mayHandle(assignee, p._id)) : professions;
}

/**
 * Who a new issue is assigned to by default: the signed-in user, as in
 * Trusstor — named from the site's users once they load (the session carries
 * only the username; D-ORCA's login is Trusstor's, so the two match).
 */
export function defaultAssignee(username: string | undefined): IIssueAssignee | null {
  return username ? { username } : null;
}

/** How an assignee reads: their name from the site's users, else what the issue holds. */
export function assigneeName(users: readonly IUser[], assignee: IIssueAssignee): string {
  return users.find((u) => u.username === assignee.username)?.name ?? assignee.name ?? assignee.username;
}
